import { getConfig } from "@/app/config";

/**
 * API origin the SPA talks to directly (cross-origin + CORS — no proxy).
 *
 * Resolved at runtime from `/config.json` (`apiUrl`), injected per-environment
 * at container start, so one built image serves every environment. Empty =
 * same-origin relative `/v1/*`. Safe to call synchronously anywhere in the app:
 * `main.tsx` awaits `loadConfig()` before the first render, and every consumer
 * runs after that (render, hooks, event handlers).
 */
export function apiBase(): string {
  try {
    return (getConfig().apiUrl ?? "").replace(/\/$/, "");
  } catch {
    // Config not loaded yet (tests, or a fetch firing before boot). Fall back to
    // same-origin relative — `main.tsx` awaits loadConfig() before first render,
    // so real requests always see the resolved origin.
    return "";
  }
}

/** Absolute API URL for a `/v1/*` path (relative when `apiUrl` is unset). */
export const apiUrl = (path: string): string => `${apiBase()}${path}`;

/**
 * Resolve a request path to the API.
 *
 * - Absolute URLs (http/https) pass through unchanged.
 * - The legacy Next proxy convention `/api/*` maps to the API — the Next
 *   `/api/[...proxy]` route that performed this rewrite was removed in the SPA
 *   migration, so call sites that still pass `/api/*` are remapped here.
 *   Unversioned `/api/x` → `/v1/x`; an already-versioned `/api/v2/x` keeps its
 *   version (→ `/v2/x`) rather than becoming `/v1/v2/x`.
 * - Any `/v{N}/*` path (remapped or passed directly) is prefixed with
 *   `apiBase()` (relative when the runtime `apiUrl` is unset → served
 *   same-origin via the edge/proxy).
 * - Any other path is returned unchanged.
 */
export function resolveApiUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  let resolved = path;
  if (path.startsWith("/api/")) {
    const rest = path.slice(5);
    resolved = /^v\d+\//.test(rest) ? `/${rest}` : `/v1/${rest}`;
  }
  return /^\/v\d+\//.test(resolved) ? `${apiBase()}${resolved}` : resolved;
}
